import { Datepicker } from "@mobiscroll/react";
import Form from "react-bootstrap/Form";
import AsyncTypeahead, { DataListType } from "../../../../components/AsyncTypeahead";
import React, { useEffect, useState } from "react";
import { ToastConfig } from "../../../../components/Toaster";
import { IdNamePair, InternalCareTeamMembers, UpdateInternalCareTeam } from "patients-experience-client";
import { createProvidersApi, createUsersApi } from "../../../../api-helpers/UserApiHelper";
import { createPatientApi } from "../../../../api-helpers/PatientApiHelper";
import Spinner from "../../../../components/Spinner";
import '../../../../assets/scss/custom/_custom.scss';
import { UI_STRINGS } from "../../../../constants/UiStrings";
interface CareTeamProps {
    selectedTeam: InternalCareTeamMembers | undefined;
    setOffCanvasShow: (show: boolean) => void;
    onSubmit: (toasterConfig: ToastConfig) => void;
    patientId: string | undefined;
}

export const EditWellinksCareTeam: React.FC<CareTeamProps> = (props: CareTeamProps) => {
    const userApi = createUsersApi();
    const providerApi = createProvidersApi();
    const patientApi = createPatientApi();
    const [usersList, setUsersList] = useState<Array<DataListType>>([]);
    const [othersList, setOthersList] = useState<Array<DataListType>>([]);
    const [providersList, setProvidersList] = useState<Array<DataListType>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [updateForm, setUpdateForm] = useState<UpdateInternalCareTeam>({
        primaryProviderId: props.selectedTeam?.primaryProvider?.id! || '',
        primaryCoachId: props.selectedTeam?.primaryCoach?.id! || '',
        otherMemberIds: props.selectedTeam?.otherMembers?.map((member: IdNamePair) => member.id) || []
    });
    const [selectedOthers, setSelectedOthers] = useState<Array<IdNamePair>>([]);
    const [validated, setValidated] = useState(false);
    const [searchError, setSearchError] = useState<string>();


    useEffect(() => {
        setSelectedOthers(props.selectedTeam?.otherMembers!);
    }, [props.selectedTeam]);

    const loadUsers = async (query: string) => {
      setSearchError(undefined);
        if (!query) {
            setUsersList([]);
            return;
        }
        setLoading(true);
        try {
            const response = await userApi.usersControllerSearchUsers( props.patientId! ,query);
            const results = response.data.map((result: any) => ({
                key: result.key,
                value: result.value,
            }));
            setUsersList(results);
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message;
            setSearchError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const loadProviders = async (query: string) => {
        if (!query) {
            setProvidersList([]);
            return;
        }
        setLoading(true);
        try {
            const response = await providerApi.providersControllerSearchProviders(query);
            const results = response.data.map((result: any) => ({
                key: result.key,
                value: result.value,
            }));
            setProvidersList(results);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const loadOthers = async (query: string) => {
        if (!query) {
            setOthersList([]);
            return;
        }
        setLoading(true);
        try {
            const response = await userApi.usersControllerSearchUsers(undefined, query);
            const results = response.data.map((result: any) => ({
                key: result.key,
                value: result.value,
            }));
            setOthersList(results);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const selectUser = (selected: any) => {
        if (!selected?.key)
            return;
        setUpdateForm({
            ...updateForm,
            primaryCoachId: selected && selected.key,
        });
    };

    const selectProvider = (selected: any) => {
        if (!selected?.key)
            return;
        setUpdateForm({
            ...updateForm,
            primaryProviderId: selected && selected.key,
        });
    };

    const selectOther = (selected: any) => {
        if (!selected?.key)
            return;
        let existingIds = updateForm.otherMemberIds;
        if(existingIds.includes(selected.key)){
            return;
        }
        existingIds.push(selected && selected.key);
        setUpdateForm({
            ...updateForm,
            otherMemberIds: existingIds
        });
        const other: IdNamePair = {id: selected.key, name: selected.value};
        setSelectedOthers((prevSelectedOthers) => [...prevSelectedOthers, other]);
    };

    const unsetProviderId = () => {
      setUpdateForm({
        ...updateForm,
        primaryProviderId: '',
      });
    }

    const unsetClinicalLiaisonId = () => {
      setUpdateForm({
        ...updateForm,
        primaryCoachId: '',
      });
    }

    const submitHandler = async (event: any) => {
        event.preventDefault();
        setValidated(true);
        const form = event.currentTarget;
        if (!form.checkValidity()) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        setLoading(true);
        try {
            await patientApi.patientsControllerUpdateInternalCareTeam(updateForm, props.patientId!);
            const toasterConfig = {
                type: "success",
                title: "Wellinks Care Team updated successfully!",
                icon: "check_circle_outline",
            };
            props.onSubmit(toasterConfig as ToastConfig);
            setTimeout(() => {
                handleDiscard();
            }, 1000);
        } catch (error) {
            const toasterConfig = {
                type: "danger",
                title: "Error in updating Wellinks Care Team!",
                icon: "error_outline",
            };
            props.onSubmit(toasterConfig as ToastConfig);
        } finally {
            setLoading(false);
        }
    };

    const handleDiscard = () => {
        props.setOffCanvasShow(false);
    };
    return (
      <>
        {loading && <Spinner />}
        <Form
          noValidate
          validated={validated}
          onSubmit={submitHandler}
          className="d-flex flex-column h-100"
        >
          <div className="flex-fill">
            <div className="row g-2 mb-3">
              <div className="col-12">
                <div className="mb-3">
                  <label className="form-label fs-12 fw-semibold mb-1">
                    Primary Provider
                  </label>
                  <AsyncTypeahead
                    name="primaryProviderId"
                    options={providersList}
                    callback={loadProviders}
                    onSelect={selectProvider}
                    defaultValue={props.selectedTeam?.primaryProvider?.name || ''}
                    placeholder={UI_STRINGS.SEARCH_PRIMARY_PROVIDER}
                    unsetId={unsetProviderId}
                    callbackOnEmpty={() => {
                      setUpdateForm({
                        ...updateForm,
                        primaryProviderId: "",
                      });
                    }}
                  ></AsyncTypeahead>
                </div>
              </div>
              <div className="col-12">
                <div className="mb-3">
                  <label className="form-label fs-12 fw-semibold mb-1">
                    Primary Clinical Liaison
                  </label>
                  <AsyncTypeahead
                    name="primaryCoachId"
                    options={usersList}
                    callback={loadUsers}
                    onSelect={selectUser}
                    defaultValue={props.selectedTeam?.primaryCoach?.name || ''}
                    callbackOnEmpty={() => {
                      setUpdateForm({
                        ...updateForm,
                        primaryCoachId: "",
                      });
                    }}
                    placeholder={UI_STRINGS.SEARCH_CLINICAL_LIAISON}
                    unsetId={unsetClinicalLiaisonId}
                    searchError={searchError}
                  ></AsyncTypeahead>
                </div>
              </div>
              <div className="col-12">
                <div className="mb-3">
                  <label className="form-label fs-12 fw-semibold mb-1">
                    Other care team members
                  </label>
                  <AsyncTypeahead
                    name="primaryCoachId"
                    options={othersList}
                    callback={loadOthers}
                    onSelect={selectOther}
                    placeholder="Search Coach"
                    resetInput={true}
                  ></AsyncTypeahead>
                </div>
                <div className="mb-3">
                  {selectedOthers?.map((other: IdNamePair) => (
                    <div className="tag d-inline-block me-1" key={other.id}>
                      <div className="tag__item is-icon">
                        <span className="user-icon">
                          <span>{other?.name?.charAt(0).toUpperCase()}</span>
                        </span>
                        {other.name}
                        <a className="tag__item-close fs-12 ms-2 me-1" href="#"
                                                onClick={
                                                    () => {
                                                        const filterted = selectedOthers.filter((item) => item.id !== other.id );
                                                        setSelectedOthers(filterted);
                                                        setUpdateForm({
                                                            ...updateForm,
                                                            otherMemberIds: filterted.map(other => other.id.toString())
                                                        });
                                                    }
                                                }>
                                                X
                                            </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="offcanvas-footer text-end w-100 border-top d-flex justify-content-between border-top">
            <div>
              <button
                className="btn btn-outline-secondary rounded-pill px-5 fs-13"
                type="reset"
                onClick={handleDiscard}
              >
                Discard
              </button>
            </div>
            <div>
              <button
                className="btn btn-secondary rounded-pill ps-4 pe-5 fs-13"
                type="submit"
              >
                Update
              </button>
            </div>
          </div>
        </Form>
      </>
    );
};
